import { useState } from "react"
import { removeContactPage, ApiError } from "../lib/api"

// "Remove page details" for a code's hosted contact page (#332, spec D9).
// Removal is not an edit: it works at any scan count, on any plan, so it shows
// even where editing is locked. The confirm is in the app, never the browser's
// own dialog, and says what the printed code will do.
export default function ContactPageRemove({ token, onRemoved, disabled = false }) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function remove() {
    setBusy(true)
    setError(null)
    try {
      const body = await removeContactPage(token)
      setConfirming(false)
      onRemoved?.(body?.code || null)
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "Couldn't remove the page. Try again.")
    } finally {
      setBusy(false)
    }
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={disabled}
        className="self-start inline-flex items-center min-h-[44px] rounded px-3 text-sm font-semibold text-fail bg-transparent hover:bg-[color:var(--color-fail)]/10 transition disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
      >
        Remove page details
      </button>
    )
  }

  return (
    <div role="alertdialog" aria-labelledby={`remove-page-${token}`} className="rounded border border-[color:var(--color-fail)] px-4 py-3 text-sm">
      <p id={`remove-page-${token}`} className="font-semibold text-coal dark:text-cloud">Remove this page's details?</p>
      <p className="mt-1 text-[color:var(--color-muted)] dark:text-muted-light">
        We erase them for good. Your printed code will show "removed by its owner" until you point it somewhere else.
      </p>
      {error && <p className="mt-2 text-fail" role="alert">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          className="inline-flex items-center min-h-[44px] rounded px-4 font-semibold text-fail border border-[color:var(--color-fail)] bg-transparent hover:bg-[color:var(--color-fail)]/10 transition disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
        >
          {busy ? "Removing…" : "Remove"}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          disabled={busy}
          className="inline-flex items-center min-h-[44px] rounded px-4 text-[color:var(--color-muted)] dark:text-muted-light hover:text-ink dark:hover:text-cloud transition focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
        >
          Keep it
        </button>
      </div>
    </div>
  )
}
